// pictionary/games/WordToImageGame.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  Grow,
  Fade,
  CardActionArea,
  Avatar,
  useTheme,
} from '@mui/material';
import { useGame } from '../../../context/GameContext';
import { green, red, grey, blue } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const WordToImageGame = ({ word, onAnswerSubmit, topic }) => {
  const { shuffleArray } = useGame();
  const theme = useTheme();
  const [options, setOptions] = useState([]);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [selectionStatus, setSelectionStatus] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);

  const wordIdRef = useRef('');

  // Reset state when the word changes
  useEffect(() => {
    if (word && topic && word.id !== wordIdRef.current) {
      console.log('Initializing WordToImageGame with new word:', word.word);
      setSelectedOptionId(null);
      setSelectionStatus(null);
      setIsAnswered(false);
      wordIdRef.current = word.id;

      // Generate options locally
      const generateOptions = () => {
        const opts = [word];
        const availableWords = topic.words.filter(w => w.id !== word.id);
        const shuffledWrong = shuffleArray([...availableWords]);
        for (let i = 0; i < Math.min(3, shuffledWrong.length); i++) {
          opts.push(shuffledWrong[i]);
        }
        return shuffleArray(opts);
      };
      setOptions(generateOptions());
    } else if (!word) {
      setOptions([]);
      setSelectedOptionId(null);
      setSelectionStatus(null);
      setIsAnswered(false);
      wordIdRef.current = '';
    }
  }, [word, topic, shuffleArray]);

  // Memoized handler for selecting an answer
  const handleAnswer = useCallback((selectedId) => {
    if (isAnswered || !word) return;

    const isCorrect = selectedId === word.id;
    setSelectedOptionId(selectedId);
    setSelectionStatus(isCorrect ? 'correct' : 'incorrect');
    setIsAnswered(true);

    // Call the parent callback
    onAnswerSubmit(isCorrect, word.word);
  }, [isAnswered, word, onAnswerSubmit]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (isAnswered) {
        // Enter key for continuing after answering
        if (e.key === 'Enter') {
          // Parent component should handle this
        }
        return;
      }

      const keyNum = parseInt(e.key, 10);
      if (!isNaN(keyNum) && keyNum >= 1 && keyNum <= options.length) {
        const index = keyNum - 1;
        if (options[index]) {
          handleAnswer(options[index].id);
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [options, handleAnswer, isAnswered]);

  if (!word) return null;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexGrow: 1,
        p: { xs: 2, sm: 3 },
        boxSizing: 'border-box',
      }}
    >
      {/* Word Display Area - More prominent */}
      <Box 
        sx={{ 
          mb: { xs: 4, sm: 5 },
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Grow in={true} timeout={600}>
          <Card
            elevation={8} // Stronger shadow for 3D effect
            sx={{
              py: 3,
              px: 5,
              borderRadius: 3,
              position: 'relative',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15), 0 8px 12px rgba(0,0,0,0.1)',
              transform: 'translateY(-5px)',
              // Bottom "layer" for 3D effect
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 4,
                right: 4,
                height: 8,
                backgroundColor: 'rgba(0,0,0,0.15)',
                borderBottomLeftRadius: 3,
                borderBottomRightRadius: 3,
                zIndex: -1,
              }
            }}
          >
            <Typography
              variant="h2"
              component="h1"
              align="center"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                textShadow: `1px 1px 3px ${grey[400]}`,
                animation: 'bounceIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '@keyframes bounceIn': {
                  '0%': { opacity: 0, transform: 'scale(0.3)' },
                  '50%': { opacity: 1, transform: 'scale(1.05)' },
                  '70%': { transform: 'scale(0.9)' },
                  '100%': { transform: 'scale(1)' },
                },
              }}
            >
              {word.word}
            </Typography>
          </Card>
        </Grow>
      </Box>

      {/* Image Options Area - HORIZONTAL ROW LAYOUT */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' }, // Row on larger screens, column on mobile
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3, // Good spacing between options
          width: '100%',
          maxWidth: 1200,
          // overflow: 'auto', // Allow horizontal scrolling if needed
          mt: 'auto',
          mb: { xs: 3, sm: 4 },
        }}
      >
        {options.map((option, index) => {
          const isSelected = selectedOptionId === option.id;
          const isCorrectSelection = isSelected && selectionStatus === 'correct';
          const isIncorrectSelection = isSelected && selectionStatus === 'incorrect';

          return (
            <Grow
              key={option.id}
              in={true}
              timeout={500}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <Box
                sx={{
                  position: 'relative',
                  minWidth: { xs: '90%', sm: '160px', md: '200px' },
                  flex: { sm: '1 0 auto' },
                  maxWidth: { sm: '220px', md: '260px' },
                  // Add bottom "layer" for 3D effect
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 5,
                    right: 5,
                    height: 8,
                    backgroundColor: 'rgba(0,0,0,0.15)',
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    zIndex: 0,
                    transition: 'all 0.2s ease',
                  },
                  // When selected, change the 3D effect
                  ...(isSelected && {
                    '&::after': {
                      backgroundColor: isCorrectSelection ? green[300] : red[300],
                      bottom: -6, // Reduced distance to show "pressing" effect
                    }
                  })
                }}
              >
                {/* Card structure with 3D effect */}
                <Card
                  elevation={isSelected ? 2 : 8} // Lower elevation when selected to simulate pressing
                  sx={{
                    height: { xs: 180, sm: 200, md: 220 },
                    width: '100%',
                    borderRadius: 3,
                    // overflow: 'hidden',
                    position: 'relative',
                    border: '3px solid',
                    borderColor: isSelected 
                      ? (isCorrectSelection ? green[500] : red[500])
                      : grey[300],
                    transition: 'all 0.25s ease-out',
                    transform: isSelected ? 'translateY(2px)' : 'translateY(-5px)', // 3D press effect
                    boxShadow: isSelected 
                      ? 'none' 
                      : '0 8px 16px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12)',
                    
                    // Dim non-selected cards when answered
                    opacity: isAnswered && !isSelected ? 0.6 : 1,
                    
                    // Hover effect only if not answered
                    ...(!isAnswered && {
                      '&:hover': {
                        borderColor: blue[300],
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.1)',
                      }
                    })
                  }}
                >
                  <CardActionArea
                    onClick={() => handleAnswer(option.id)}
                    disabled={isAnswered}
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 1.5,
                      position: 'relative',
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={option.image}
                      alt={`Option for ${word.word}: ${option.word}`}
                      sx={{
                        maxWidth: '90%',
                        maxHeight: '90%',
                        objectFit: 'contain',
                        borderRadius: 1,
                        zIndex: 1,
                      }}
                    />

                    {/* Feedback Overlay & Icon */}
                    {isSelected && (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            bgcolor: isCorrectSelection ? green[100] : red[100],
                            opacity: 0.4,
                            zIndex: 2,
                          }}
                        />
                        <Fade in={true} timeout={300}>
                          <Avatar
                            sx={{
                              position: 'absolute',
                              top: '50%', left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: { xs: 50, sm: 60 },
                              height: { xs: 50, sm: 60 },
                              bgcolor: isCorrectSelection ? green[600] : red[600],
                              zIndex: 3,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                            }}
                          >
                            {isCorrectSelection ? (
                              <CheckCircleIcon sx={{ color: 'white', fontSize: { xs: '2rem', sm: '2.5rem' } }} />
                            ) : (
                              <CancelIcon sx={{ color: 'white', fontSize: { xs: '2rem', sm: '2.5rem' } }} />
                            )}
                          </Avatar>
                        </Fade>
                      </>
                    )}

                    {/* Number Badge */}
                    {!isAnswered && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          p: '3px 8px',
                          borderRadius: '8px',
                          lineHeight: 1,
                          zIndex: 4,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                        }}
                      >
                        {index + 1}
                      </Box>
                    )}
                  </CardActionArea>
                </Card>
              </Box>
            </Grow>
          );
        })}
      </Box>
    </Box>
  );
};

export default WordToImageGame;